import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";
import {
  CalendarIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline";

const capitalizeFirst = (word = "") =>
  word ? word.charAt(0).toUpperCase() + word.slice(1) : "";

const formatRange = (start, end) => {
  if (!start) return "";
  const formatEventDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const str = date.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const parts = str.split(" ");
    if (parts.length >= 4) {
      parts[0] = capitalizeFirst(parts[0]);
      parts[2] = capitalizeFirst(parts[2]);
      return parts.join(" ");
    }
    return capitalizeFirst(str);
  };

  const startFmt = formatEventDate(start);
  const endFmt = end && end !== start ? formatEventDate(end) : "";
  return endFmt ? `${startFmt} - ${endFmt}` : startFmt;
};

const Eventi = () => {
  const { user, activeCongregazione } = useAuth();
  const isAdmin = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const [loading, setLoading] = useState(true);
  const [eventi, setEventi] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    data_inizio: "",
    data_fine: "",
    note: "",
    mostra_in_bacheca: false,
  });

  const canCreate = useMemo(() => {
    if (!isAdmin) return false;
    if (user?.ruolo === "super_admin") {
      return Boolean(activeCongregazione?.id);
    }
    return true;
  }, [isAdmin, user?.ruolo, activeCongregazione?.id]);
  const eventCountLabel = loading
    ? "Caricamento..."
    : `${eventi.length} ${eventi.length === 1 ? "evento" : "eventi"}`;

  const loadEventi = async () => {
    try {
      setLoading(true);
      const res = await api.get("/eventi");
      setEventi(res.data?.eventi || []);
    } catch (err) {
      console.error("Errore caricamento eventi:", err);
      toastError(err.response?.data?.message || "Errore nel caricamento degli eventi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadEventi();
    }
  }, [user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (user?.ruolo === "super_admin" && !activeCongregazione?.id) {
      toastError("Seleziona una congregazione attiva per creare un evento.");
      return;
    }
    if (!form.nome.trim()) {
      toastError("Inserisci il nome evento");
      return;
    }
    if (!form.data_inizio) {
      toastError("Inserisci la data evento");
      return;
    }
    if (form.data_fine && form.data_fine < form.data_inizio) {
      toastError("La data finale non può essere precedente alla data iniziale");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        data_inizio: form.data_inizio,
        data_fine: form.data_fine || null,
        note: form.note?.trim() || "",
        mostra_in_bacheca: form.mostra_in_bacheca,
        ...(user?.ruolo === "super_admin" && activeCongregazione?.id
          ? { congregazione_id: activeCongregazione.id }
          : {}),
      };

      if (editingId) {
        await api.patch(`/eventi/${editingId}`, payload);
        toastSuccess("Evento aggiornato");
      } else {
        await api.post("/eventi", payload);
        toastSuccess("Evento creato con successo");
      }
      setShowModal(false);
      setEditingId(null);
      setForm({
        nome: "",
        data_inizio: "",
        data_fine: "",
        note: "",
        mostra_in_bacheca: false,
      });
      loadEventi();
    } catch (err) {
      console.error("Errore salvataggio evento:", err);
      toastError(
        err.response?.data?.message ||
          (editingId ? "Errore nell'aggiornamento dell'evento" : "Errore nella creazione dell'evento")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const startEdit = (evento) => {
    setEditingId(evento.id);
    setForm({
      nome: evento.nome || "",
      data_inizio: evento.data_inizio || "",
      data_fine: evento.data_fine || "",
      note: evento.note || "",
      mostra_in_bacheca: Boolean(evento.mostra_in_bacheca),
    });
    setShowModal(true);
  };

  const handleSendPush = async (eventoId) => {
    if (!isAdmin || !eventoId) return;
    setSendingId(eventoId);
    try {
      await api.post("/push/eventi/send", { evento_id: eventoId });
      toastSuccess("Notifica evento inviata");
    } catch (err) {
      console.error("Errore invio notifica evento:", err);
      toastError(err.response?.data?.message || "Errore invio notifica evento");
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Sei sicuro di voler eliminare questo evento?")) {
      return;
    }
    setDeletingId(id);
    try {
      await api.delete(`/eventi/${id}`);
      toastSuccess("Evento eliminato");
      if (expandedId === id) {
        setExpandedId(null);
      }
      loadEventi();
    } catch (err) {
      console.error("Errore eliminazione evento:", err);
      toastError(err.response?.data?.message || "Errore nell'eliminazione dell'evento");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 md:px-6 pt-4 md:pt-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Eventi</h1>
        <p className="text-gray-600 mt-1">
          Consulta gli eventi di congregazione.
          {isAdmin && (
            <span className="block sm:inline">
              {" "}
              I proclamatori vedono solo in lettura.
            </span>
          )}
        </p>
        <div className="accent-line mt-3" />
      </div>

      <div className="bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-md">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <span className="font-semibold text-gray-900">
              Calendario eventi
            </span>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
            {eventCountLabel}
          </span>
        </div>
        {isAdmin && (
          <div className="w-full sm:w-auto flex sm:justify-end">
            <button
              onClick={() => setShowModal(true)}
              disabled={!canCreate}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-60"
              title={
                canCreate
                  ? "Aggiungi un nuovo evento"
                  : "Seleziona una congregazione per creare eventi"
              }
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Aggiungi Evento
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {isAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3 text-blue-800">
              <BellAlertIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              <p className="text-sm">
                Puoi inviare una notifica push per richiamare l’attenzione
                sull’evento.
              </p>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-5 bg-gray-200 rounded w-1/3" />
            {[1, 2, 3].map((k) => (
              <div key={k} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      ) : eventi.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Nessun evento disponibile</p>
          {isAdmin && (
            <p className="text-sm text-gray-500 mt-1">
              Crea un nuovo evento per mostrarlo ai proclamatori.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {eventi.map((evento) => {
            const expanded = expandedId === evento.id;
            return (
              <div
                key={evento.id}
                className="relative bg-white border border-gray-200 rounded-lg shadow overflow-hidden"
              >
                <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
                <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {evento.nome}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-700 mt-1">
                      <CalendarIcon className="h-5 w-5 text-primary-600" />
                      <span>{formatRange(evento.data_inizio, evento.data_fine)}</span>
                    </div>
                    {evento.mostra_in_bacheca && (
                      <span className="mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Mostra in bacheca
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 sm:flex-row sm:justify-end sm:w-auto w-full pt-3 border-t sm:border-t-0 sm:pt-0 justify-center">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(evento.id)}
                      className="h-9 w-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      title={expanded ? "Nascondi dettagli" : "Mostra dettagli"}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleSendPush(evento.id)}
                          disabled={sendingId === evento.id}
                          className="h-9 w-9 flex items-center justify-center rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-60"
                          title="Invia notifica push evento"
                        >
                          {sendingId === evento.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          ) : (
                            <BellAlertIcon className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => startEdit(evento)}
                          className="h-9 w-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                          title="Modifica evento"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(evento.id)}
                          disabled={deletingId === evento.id}
                          className="h-9 w-9 flex items-center justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:opacity-60"
                          title="Elimina evento"
                        >
                          {deletingId === evento.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <TrashIcon className="h-5 w-5" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {expanded && (
                  <div className="border-t border-gray-200 p-4 sm:p-5 bg-gray-50">
                    {evento.note ? (
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {evento.note}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Nessuna nota.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">Nuovo Evento</p>
                <h3 className="text-lg font-semibold text-gray-900">Aggiungi Evento</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Chiudi modal evento"
              >
                ✕
              </button>
            </div>
            <form className="p-6 space-y-5" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Evento
                </label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  maxLength={255}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inizio
                  </label>
                  <input
                    type="date"
                    value={form.data_inizio}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, data_inizio: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Fine (opzionale)
                  </label>
                  <input
                    type="date"
                    value={form.data_fine}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, data_fine: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  rows={4}
                  value={form.note}
                  onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Dettagli dell'evento (visibili ai proclamatori)"
                />
              </div>
              <div className="flex items-center">
                <input
                  id="mostra_bacheca"
                  type="checkbox"
                  checked={form.mostra_in_bacheca}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, mostra_in_bacheca: e.target.checked }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="mostra_bacheca" className="ml-2 text-sm text-gray-700">
                  Mostra evento in bacheca
                </label>
              </div>
              {user?.ruolo === "super_admin" && (
                <p className="text-xs text-gray-500">
                  Usa la congregazione attiva per associare l'evento.
                </p>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  disabled={submitting}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-60"
                  disabled={submitting || !canCreate}
                >
                  {submitting ? "Salvataggio..." : "Crea Evento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Eventi;
